import { Link } from "react-router-dom";
import { Trophy, Microphone, Brain, FilmSlate, Lightning, Users } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

export default function Landing() {
    const { user } = useAuth();
    return (
        <div className="min-h-screen bg-wta-bg text-white">
            {/* Header */}
            <header className="absolute top-0 left-0 right-0 z-10 px-6 md:px-12 py-5 flex items-center justify-between">
                <Link to="/" data-testid="landing-logo" className="flex items-center gap-2">
                    <img src="/wta-logo.png" alt="World Talent Arena" className="h-10 md:h-12 w-auto" />
                </Link>
                <div className="flex items-center gap-3">
                    {user ? (
                        <Link to="/feed" data-testid="enter-arena-btn" className="btn-primary px-6 py-2.5 rounded-sm text-sm">
                            Enter Arena
                        </Link>
                    ) : (
                        <>
                            <Link to="/login" data-testid="landing-login-btn" className="text-sm hover:text-wta-primary transition">
                                Log in
                            </Link>
                            <Link to="/register" data-testid="landing-register-btn" className="btn-primary px-5 py-2 rounded-sm text-sm">
                                Sign up
                            </Link>
                        </>
                    )}
                </div>
            </header>

            {/* Hero */}
            <section className="relative min-h-[100vh] flex items-end overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1774988353278-b01caf489c4e"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-wta-bg via-wta-bg/60 to-wta-bg/80" />
                <div className="relative max-w-5xl mx-auto px-6 md:px-12 pb-32 md:pb-20 pt-32">
                    <div className="font-heading text-xs tracking-[0.4em] text-wta-accent mb-6">SEASON 01 · LIVE NOW</div>
                    <h1 className="font-heading text-5xl sm:text-7xl md:text-[120px] leading-[0.85] tracking-tight uppercase">
                        World <span className="text-wta-primary">Talent</span><br />
                        Arena
                    </h1>
                    <p className="mt-8 text-base md:text-lg text-white/70 max-w-2xl">
                        Sing. Dance. Rap. Code. Compete head-to-head in 32-player tournaments.
                        Earn coins, win prize pools, build your global ranking.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-3">
                        <Link to={user ? "/competitions" : "/register"} data-testid="hero-cta-join" className="btn-primary px-8 py-4 rounded-sm text-xl">
                            Join the Arena
                        </Link>
                        <Link to="/feed" data-testid="hero-cta-watch" className="btn-ghost px-8 py-4 rounded-sm text-base font-heading uppercase tracking-wider">
                            Watch Feed
                        </Link>
                    </div>
                </div>
            </section>

            {/* Categories */}
            <section className="px-6 md:px-12 py-20 max-w-6xl mx-auto">
                <div className="font-heading text-xs tracking-[0.4em] text-wta-muted mb-3">COMPETE IN</div>
                <h2 className="font-heading text-4xl md:text-6xl tracking-tight uppercase mb-10">7 Categories. 1 Crown.</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        { i: Microphone, label: "Singing" },
                        { i: Lightning, label: "Dancing" },
                        { i: FilmSlate, label: "Comedy" },
                        { i: Microphone, label: "Rap" },
                        { i: Users, label: "Modeling" },
                        { i: Brain, label: "Trivia" },
                        { i: Lightning, label: "Invention" },
                        { i: Trophy, label: "Superstar" },
                    ].map(({ i: Icon, label }) => (
                        <div key={label} className="bg-wta-surface border border-wta-border p-6 rounded-sm hover:border-wta-primary transition group">
                            <Icon size={32} className="text-wta-muted group-hover:text-wta-primary transition" />
                            <div className="mt-4 font-heading text-2xl tracking-wide">{label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Tier ladder */}
            <section className="px-6 md:px-12 py-20 max-w-6xl mx-auto">
                <h2 className="font-heading text-4xl md:text-6xl tracking-tight uppercase mb-3">Pick Your Tier</h2>
                <p className="text-white/60 mb-10 max-w-xl">70% of every entry fee goes to the prize pool. Win it all.</p>
                <div className="grid md:grid-cols-4 gap-4">
                    {[
                        { tier: "Entry", fee: "$10", color: "border-white/20" },
                        { tier: "Standard", fee: "$50", color: "border-wta-secondary" },
                        { tier: "Elite", fee: "$100", color: "border-wta-primary" },
                        { tier: "Superstar", fee: "$1000", color: "border-wta-accent" },
                    ].map((t) => (
                        <div key={t.tier} className={`bg-wta-surface border-2 ${t.color} p-6 rounded-sm`}>
                            <div className="font-heading text-3xl tracking-wide">{t.tier}</div>
                            <div className="font-heading text-5xl mt-3">{t.fee}</div>
                            <div className="text-xs text-wta-muted mt-1 tracking-widest uppercase">Per Entry</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA Footer */}
            <section className="px-6 md:px-12 py-32 pb-40 md:pb-32 text-center bg-wta-surface border-t border-wta-border">
                <h2 className="font-heading text-5xl md:text-7xl tracking-tight uppercase">Your stage awaits.</h2>
                <Link to={user ? "/competitions" : "/register"} data-testid="footer-cta" className="inline-block mt-10 btn-primary px-10 py-4 rounded-sm text-xl">
                    {user ? "Browse Competitions" : "Create your account"}
                </Link>
            </section>
        </div>
    );
}
